import Typography from 'components/Typography'
import styles from './CityInfo.module.scss'
import { FC, ReactNode } from 'react'
import Anchor, { TAnchorElementProps } from 'components/Anchor'
import List from 'components/List'
import { searchCityInfoClick } from 'pages/imoveis/events'

type TDescription = {
  [key: string]: ReactNode
}

type TItem = {
  size: string
  title: string
  content: ReactNode
}

type TFAQProps = {
  [key: string]: Array<TItem>
}

type TClickAnchor = {
  href: string
  view: 'text' | 'faq'
  children: ReactNode
} & Pick<TAnchorElementProps, 'target' | 'onClick'>

const ClickEventAnchor: FC<TClickAnchor> = ({ href, view, children }) => {
  return (
    <Anchor href={href} onClick={() => searchCityInfoClick({ href, view })}>
      {children}
    </Anchor>
  )
}

export const cityDescription: TDescription = {
  'sao-paulo': (
    <div className={styles.ecCityDescription}>
      <Typography variant="ParagraphSmall">
        São Paulo, é a maior cidade do Brasil em população e oferece uma ampla
        diversidade de estilos de vida. Por isso, adquirir um imóvel na cidade
        exige um conhecimento profundo dos bairros e dos diversos tipos de
        propriedades disponíveis. É por isso que a EmCasa está à disposição para
        auxiliá-lo na compra de um imóvel na metrópole que nunca descansa. Neste
        guia, vamos te ajudar a encontrar o melhor bairro para você. Você está
        pronto para descobrir mais sobre São Paulo?
      </Typography>
      <Typography as="h4" variant="ParagraphSmall">
        <strong>Melhores bairros para morar em São Paulo</strong>
      </Typography>
      <Typography variant="ParagraphSmall">
        São Paulo é dividida em cinco principais zonas, cada uma com suas
        características distintas: Centro, Zona Norte, Zona Sul, Zona Oeste e
        Zona Leste.
      </Typography>
      <List
        className={styles.ecInterestList}
        list={[
          <Typography variant="ParagraphSmall" key="sp-centro">
            <strong>Centro:</strong> A zona mais antiga da cidade, atualmente
            passando por revitalização, oferece diversas opções de moradia e
            lazer. Bairros notáveis incluem{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/bela-vista"
              view="text"
            >
              Bela Vista
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/liberdade"
              view="text"
            >
              Liberdade
            </ClickEventAnchor>
            ;
          </Typography>,
          <Typography variant="ParagraphSmall" key="sp-zona-norte">
            <strong>Zona Norte:</strong> Possui um variado comércio e está em
            processo de urbanização em algumas regiões. Bairros conhecidos
            incluem{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/santana"
              view="text"
            >
              Santana
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/tucuruvi"
              view="text"
            >
              Tucuruvi
            </ClickEventAnchor>
            ;
          </Typography>,
          <Typography variant="ParagraphSmall" key="sp-zona-sul">
            <strong>Zona Sul:</strong> A maior zona da cidade, com uma ampla
            diversidade de moradia, incluindo áreas nobres como{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/vila-mariana"
              view="text"
            >
              Vila Mariana
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/morumbi"
              view="text"
            >
              Morumbi
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/santo-amaro"
              view="text"
            >
              Santo Amaro
            </ClickEventAnchor>
            ;
          </Typography>,
          <Typography variant="ParagraphSmall" key="sp-zona-oeste">
            <strong>Zona Oeste:</strong> Ideal para quem busca opções culturais,
            com shoppings, museus, galerias e escolas. Bairros de destaque
            incluem{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/pinheiros"
              view="text"
            >
              Pinheiros
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/perdizes"
              view="text"
            >
              Perdizes
            </ClickEventAnchor>
            ;
          </Typography>,
          <Typography variant="ParagraphSmall" key="zone-leste">
            <strong>Zona Leste:</strong> Conhecida como &quot;ZL&quot; é uma das
            zonas mais culturalmente diversas da cidade, resultado da imigração
            no século XIX. Alguns bairros notáveis são{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/itaquera"
              view="text"
            >
              Itaquera
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/tatuape"
              view="text"
            >
              Tatuapé
            </ClickEventAnchor>
            .
          </Typography>,
        ]}
      ></List>
      <Typography as="h4" variant="ParagraphSmall">
        <strong>Como é viver em São Paulo?</strong>
      </Typography>
      <Typography variant="ParagraphSmall">
        Viver na maior cidade da América Latina é uma experiência que envolve
        diversidade, cultura e oportunidades, já que São Paulo oferece uma vida
        rica e bem dinâmica para os seus moradores. Visitantes de todas as
        regiões do Brasil convergem para São Paulo, e a primeira impressão que
        se tem ao adentrar a cidade é a marcante e enriquecedora diversidade
        étnica e cultural. Este mosaico de influências se traduz de forma
        magnífica na cena gastronômica paulistana, onde se encontram
        estabelecimentos capazes de satisfazer os mais exigentes paladares.
        Bairros como{' '}
        <ClickEventAnchor
          href="/regioes-atendidas/sp/sao-paulo/vila-mariana"
          view="text"
        >
          Vila Mariana
        </ClickEventAnchor>
        ,{' '}
        <ClickEventAnchor
          href="/regioes-atendidas/sp/sao-paulo/mooca"
          view="text"
        >
          Mooca
        </ClickEventAnchor>
        ,{' '}
        <ClickEventAnchor
          href="/regioes-atendidas/sp/sao-paulo/liberdade"
          view="text"
        >
          Liberdade
        </ClickEventAnchor>{' '}
        e{' '}
        <ClickEventAnchor
          href="/regioes-atendidas/sp/sao-paulo/cerqueira-cesar"
          view="text"
        >
          Cerqueira César
        </ClickEventAnchor>{' '}
        se destacam como verdadeiros oásis culinários, abrigando alguns dos
        restaurantes mais prestigiados da cidade.
      </Typography>
      <Typography variant="ParagraphSmall">
        São Paulo oferece uma ampla variedade de opções de lazer e
        entretenimento, incluindo três estádios de futebol. Entre eles, o
        Allianz Parque, localizado no bairro da Água Branca, também serve como
        palco internacional para shows de artistas famosos, como Taylor Swift e
        Justin Bieber. Adicionalmente, os parques Ibirapuera, localizado na
        pitoresca Vila Mariana, e Villa-Lobos, no encantador bairro de Alto de
        Pinheiros, oferecem uma fuga revigorante da agitação urbana. São
        refúgios naturais que convidam a explorar o contato com a natureza,
        proporcionando um merecido descanso em meio à exuberância verde da
        cidade.
      </Typography>
      <Typography variant="ParagraphSmall">
        Resumindo, viver em São Paulo é uma experiência única, repleta de opções
        de entretenimento, diversidade e cultura. A cidade é uma escolha ideal
        para você que busca uma vida cosmopolita, ritmo acelerado e várias
        oportunidades. Confira os{' '}
        <ClickEventAnchor
          target="blank"
          href="/regioes-atendidas/sp/sao-paulo"
          view="text"
        >
          bairros de São Paulo
        </ClickEventAnchor>{' '}
        que a EmCasa tem imóveis disponíveis para você visitar.
      </Typography>
    </div>
  ),
  'rio-de-janeiro': (
    <div className={styles.ecCityDescription}>
      <Typography variant="ParagraphSmall">
        O Rio de Janeiro, como uma das principais cidades do país, oferece um
        clima tropical, praias magníficas e pontos turísticos que são
        verdadeiros cartões postais. No entanto, comprar um imóvel na cidade
        maravilhosa vai exigir ter um conhecimento sobre os bairros e dos
        imóveis disponíveis.
      </Typography>
      <Typography as="h4" variant="ParagraphSmall" bold>
        Bairros do Rio de Janeiro
      </Typography>
      <Typography variant="ParagraphSmall">
        O Rio de Janeiro é uma cidade diversificada, com uma ampla gama de
        bairros, dividido por zonas, cada um com suas características únicas e
        atrativas:
      </Typography>
      <List
        className={styles.ecInterestList}
        list={[
          <Typography variant="ParagraphSmall" key="rj-zona-sul">
            <strong>Zona Sul:</strong> É uma das regiões mais emblemáticas e
            reconhecidas da cidade, conhecida por suas praias deslumbrantes,
            atmosfera sofisticada e estilo de vida vibrante. Os bairros mais
            conhecidos dessa Zona são{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/ipanema"
              view="text"
            >
              Ipanema
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/leblon"
              view="text"
            >
              Leblon
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/copacabana"
              view="text"
            >
              Copacabana
            </ClickEventAnchor>
            .
          </Typography>,
          <Typography variant="ParagraphSmall" key="rj-zona-norte">
            <strong>Zona Norte:</strong> Região diversificada e ampla que
            oferece uma atmosfera única em comparação com a Zona Sul da cidade.
            Esta área é caracterizada por sua mistura de bairros residenciais,
            vida urbana, cultura e tradição. Alguns dos bairros mais notáveis na
            Zona Norte incluem{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/tijuca"
              view="text"
            >
              Tijuca
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/vila-isabel"
              view="text"
            >
              Vila Isabel
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/meier"
              view="text"
            >
              Méier
            </ClickEventAnchor>{' '}
            e o histórico bairro de{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/santa-teresa"
              view="text"
            >
              Santa Teresa
            </ClickEventAnchor>
            .
          </Typography>,
          <Typography variant="ParagraphSmall" key="rj-zona-oeste">
            <strong>Zona Oeste:</strong> Uma região que está se tornando uma
            área de expansão da cidade. Ela é caracterizada por seu
            desenvolvimento urbano, praias amplas, centros comerciais e uma
            atmosfera mais moderna. Alguns dos bairros da Zona Oeste são{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/barra-da-tijuca"
              view="text"
            >
              Barra da Tijuca
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/recreio-dos-bandeirantes"
              view="text"
            >
              Recreio dos Bandeirantes
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/jacarepagua"
              view="text"
            >
              Jacarepaguá
            </ClickEventAnchor>
            .
          </Typography>,
          <Typography variant="ParagraphSmall" key="rj-centro">
            <strong>Centro:</strong> É o coração histórico e comercial da cidade
            do Rio de Janeiro, uma área rica em cultura, arquitetura histórica e
            vitalidade urbana. Essa região é caracterizada por sua mistura de
            prédios históricos bem preservados e arranha-céus modernos, criando
            uma paisagem urbana diversificada. Alguns pontos notáveis no centro
            incluem o bairro da{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/lapa"
              view="text"
            >
              Lapa
            </ClickEventAnchor>
            , a região da{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/gloria"
              view="text"
            >
              Glória
            </ClickEventAnchor>{' '}
            e a região do porto revitalizada.
          </Typography>,
        ]}
      />
      <Typography as="h4" variant="ParagraphSmall" bold>
        <strong>Como é viver no Rio de Janeiro?</strong>
      </Typography>
      <Typography variant="ParagraphSmall">
        Viver no Rio de Janeiro é experimentar uma das cidades mais emocionantes
        e vibrantes do Brasil e do mundo. A &quot;Cidade Maravilhosa&quot; é
        famosa por suas praias deslumbrantes, paisagens naturais impressionantes
        e um estilo de vida único.
      </Typography>
      <Typography variant="ParagraphSmall">
        O que mais chama atenção na cidade, sem dúvidas alguma, são as praias, o
        carioca é muito abençoado por ter as melhores praias, incluindo{' '}
        <ClickEventAnchor
          href="/regioes-atendidas/rj/rio-de-janeiro/copacabana"
          view="text"
        >
          Copacabana
        </ClickEventAnchor>
        ,{' '}
        <ClickEventAnchor
          href="/regioes-atendidas/rj/rio-de-janeiro/ipanema"
          view="text"
        >
          Ipanema
        </ClickEventAnchor>{' '}
        e{' '}
        <ClickEventAnchor
          href="/regioes-atendidas/rj/rio-de-janeiro/leblon"
          view="text"
        >
          Leblon
        </ClickEventAnchor>
        . Os moradores se deslocam até lá para praticar esportes, socializar ou
        simplesmente relaxar à beira mar.
      </Typography>
      <Typography variant="ParagraphSmall">
        Além das praias, o Rio de Janeiro deslumbra com sua efervescente cena
        cultural. O cenário inclui museus notáveis, como o futurístico Museu do
        Amanhã, situado no coração da cidade, e o inspirador Museu de Arte
        Moderna. A cidade ostenta bibliotecas magníficas, incluindo a renomada
        Biblioteca Nacional, e teatros de renome, como o icônico Theatro
        Municipal do Rio de Janeiro, que consolida sua posição como um
        verdadeiro epicentro cultural.
      </Typography>
      <Typography variant="ParagraphSmall">
        Na parte de gastronomia, o Rio oferece uma culinária diversificada,
        abrangendo pratos típicos brasileiros, como a feijoada, até culinárias
        internacionais de ótima qualidade. O polo gastronômico mais conhecido no
        Rio de Janeiro é o de Vista Alegre que fica na zona norte da cidade.
      </Typography>
      <Typography variant="ParagraphSmall">
        E um dos carros chefe para quem mora no Rio são as festas e celebrações,
        como o famoso Carnaval do Rio, Rock In Rio e o Réveillon de Copacabana
        que reúne pessoas do mundo inteiro para ver a queima de fogos.
      </Typography>
      <Typography variant="ParagraphSmall">
        Viver no Rio de Janeiro é viver numa cidade muito gostosa e acolhedora,
        repleta de beleza natural, cultura e energia. Para quem busca uma
        mistura de vida urbana e natureza deslumbrante, o Rio de Janeiro é um
        lugar que encanta e cativa.
      </Typography>
      <Typography variant="ParagraphSmall">
        Confira{' '}
        <ClickEventAnchor
          href="https://emcasa.com/regioes-atendidas/rj/rio-de-janeiro"
          view="text"
        >
          os bairros do Rio de Janeiro
        </ClickEventAnchor>{' '}
        que a EmCasa tem imóveis disponíveis para você visitar.
      </Typography>
    </div>
  ),
}

const defaultProps = {
  size: 'small',
  customElements: {
    title: {
      as: 'h4',
    },
    disableToggleTitle: true,
  },
}

export const FAQ: TFAQProps = {
  'sao-paulo': [
    {
      ...defaultProps,
      title: 'Quais são os bairros mais seguros de morar em São Paulo?',
      content: (
        <div className={styles.ecFaqItem}>
          <Typography variant="ParagraphSmall">
            São Paulo tem muitos bairros considerados seguros para se viver,
            como{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/jardins"
              view="faq"
            >
              Jardins
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/vila-nova-conceicao"
              view="faq"
            >
              Vila Nova Conceição
            </ClickEventAnchor>
            <ClickEventAnchor
              target="blank"
              href="/regioes-atendidas/sp/sao-paulo/moema"
              view="faq"
            >
              Moema
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/higienopolis"
              view="faq"
            >
              Higienópolis
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/perdizes"
              view="faq"
            >
              Perdizes
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/brooklin"
              view="faq"
            >
              Brooklin
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/vila-mariana"
              view="faq"
            >
              Vila Mariana
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/alto-de-pinheiros"
              view="faq"
            >
              Alto de Pinheiros
            </ClickEventAnchor>
            . Ao escolher o bairro que você deseja comprar o seu imóvel, faça
            uma pesquisa detalhada sobre a vizinhança, e sempre tome medidas de
            segurança pessoal e residencial para evitar transtornos.
          </Typography>
        </div>
      ),
    },
    {
      ...defaultProps,
      title: 'Como é o sistema de transporte público na cidade de São Paulo?',
      content: (
        <div className={styles.ecFaqItem}>
          <Typography variant="ParagraphSmall">
            O sistema de transporte público da cidade de São Paulo é notável por
            sua extensão, eficiência e ser multimodal. A rede de ônibus é
            extensa e atende todos os bairros da cidade. Conectado aos ônibus, a
            CPTM (sistema de trens) é uma alternativa conveniente para trajetos
            maiores, conectando até cidades vizinhas, como{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/guarulhos"
              view="faq"
            >
              Guarulhos
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/barueri"
              view="faq"
            >
              Barueri
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/jundiai"
              view="faq"
            >
              Jundiaí
            </ClickEventAnchor>
            . Completando o sistema, o metrô de São Paulo, que é o maior da
            América Latina, conecta os principais pontos da metrópole de forma
            prática e eficaz.
          </Typography>
          <Typography variant="ParagraphSmall">
            As linhas de metrôs da cidade de São Paulo atualmente são:
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Linha Vermelha</strong>: Também conhecida como Linha 3 do
            metrô de São Paulo, é a espinha dorsal da cidade, conectando a Zona
            Leste à Zona Oeste. Ela também serve quem chega a São Paulo pela
            rodoviária da Barra Funda. Essa linha atende áreas residenciais,
            comerciais e culturais da cidade, passando por bairros famosos como:{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/barra-funda"
              view="faq"
            >
              Barra Funda
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/santa-cecilia"
              view="faq"
            >
              Santa Cecília
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/tatuape"
              view="faq"
            >
              Tatuapé
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/vila-matilde"
              view="faq"
            >
              Vila Matilde
            </ClickEventAnchor>
            .
          </Typography>
          <Typography variant="ParagraphSmall">
            Se quiser saber mais sobre a Linha Vermelha, a EmCasa preparou um
            post completo sobre as suas estações, confira{' '}
            <ClickEventAnchor
              target="blank"
              href="https://emcasa.com/blog/guia-de-bairros/linha-3-vermelha-do-metro-de-sao-paulo-guia-completo/"
              view="faq"
            >
              aqui
            </ClickEventAnchor>
            .
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Linha Azul</strong>: A Linha 1 do metrô de São Paulo conecta
            a zona norte à zona sul, sendo uma rota vital no deslocamento do dia
            a dia, ligando bairros e pontos estratégicos, como a estação
            Portuguesa-Tietê, que abriga a rodoviária interestadual. Os bairros
            mais conhecidos dessa linha são:{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/jabaquara"
              view="faq"
            >
              Jabaquara
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/ana-rosa"
              view="faq"
            >
              Ana Rosa
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/paraiso"
              view="faq"
            >
              Paraíso
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/santana"
              view="faq"
            >
              Santana
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/praca-da-arvore"
              view="faq"
            >
              Praça da Árvore
            </ClickEventAnchor>
            .
          </Typography>
          <Typography variant="ParagraphSmall">
            Se você quiser saber mais sobre a Linha Azul, a EmCasa preparou um
            post falando de cada estação que a linha possui, confira{' '}
            <ClickEventAnchor
              target="blank"
              view="faq"
              href="https://emcasa.com/blog/guia-de-bairros/linha1-azul-metro-sao-paulo-guia-completo/"
            >
              aqui
            </ClickEventAnchor>
            .
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Linha Verde</strong>: Linha Verde: A Linha 2 Verde de São
            Paulo, conecta a Zona Leste à Zona Central. Ela une áreas
            residenciais e comerciais e desempenha um papel fundamental no
            deslocamento diário para o trabalho. Os principais bairros dessa
            linha são:{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/sumare"
              view="faq"
            >
              Sumaré
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/vila-madalena"
              view="faq"
            >
              Vila Madalena
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/vila-prudente"
              view="faq"
            >
              Vila Prudente
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/sacoma"
              view="faq"
            >
              Sacomã
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/alto-do-ipiranga"
              view="faq"
            >
              Alto do Ipiranga
            </ClickEventAnchor>
            .
          </Typography>
          <Typography variant="ParagraphSmall">
            Se você quiser saber mais sobre a Linha Verde, a EmCasa preparou um
            post falando de cada estação que a linha possui, confira{' '}
            <ClickEventAnchor
              target="blank"
              view="faq"
              href="https://emcasa.com/blog/guia-de-bairros/linha-2-verde-do-metro-de-sao-paulo-guia-completo/"
            >
              aqui
            </ClickEventAnchor>
            .
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Linha Amarela</strong>: A Linha 4 do metrô de São Paulo,
            inaugurada em 2011, é a linha mais nova e eficiente do metrô
            paulistano. Conectando a Zona Norte à Zona Oeste, ela facilita o
            acesso a áreas residenciais, comerciais e centros de entretenimento.
            Os principais bairros que essa linha atende são:{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/morumbi"
              view="faq"
            >
              Morumbi
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/oscar-freire"
              view="faq"
            >
              Oscar Freire
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/pinheiros"
              view="faq"
            >
              Pinheiros
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/faria-lima"
              view="faq"
            >
              Faria Lima
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/fradique-coutinho"
              view="faq"
            >
              Fradique Coutinho
            </ClickEventAnchor>
            .
          </Typography>
          <Typography variant="ParagraphSmall">
            Se você quiser saber mais sobre a Linha Amarela, a EmCasa preparou
            um post falando de cada estação que a linha possui, confira{' '}
            <ClickEventAnchor
              target="blank"
              view="faq"
              href="https://emcasa.com/blog/guia-de-bairros/linha-4-amarela-do-metro-de-sao-paulo-guia-completo/"
            >
              aqui
            </ClickEventAnchor>
            .
          </Typography>
        </div>
      ),
    },
    {
      ...defaultProps,
      title:
        'Quais são as opções de lazer, cultura e entretenimento em São Paulo?',
      content: (
        <div className={styles.ecFaqItem}>
          <Typography variant="ParagraphSmall">
            São Paulo oferece uma vasta opções de entretenimento, desde de
            museus até parques, abaixo listamos 5 opções de entretenimento na
            cidade de São Paulo:
          </Typography>

          <Typography variant="ParagraphSmall">
            <strong>MASP:</strong> O famoso Museu de Arte de São Paulo é uma
            ótima opção para os amantes de arte, fica localizado na Av. Paulista
            no bairro{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/bela-vista"
              view="faq"
            >
              Bela Vista
            </ClickEventAnchor>
            ;
          </Typography>

          <Typography variant="ParagraphSmall">
            <strong>Parque Villa-Lobos:</strong> Localizado no bairro{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/alto-dos-pinheiros"
              view="faq"
            >
              Alto dos Pinheiros
            </ClickEventAnchor>
            , o parque conta com uma vasta área verde e espaços para praticar
            esportes, piqueniques e demais atividades ao ar livre, além de
            eventualmente sediar eventos e shows;
          </Typography>

          <Typography variant="ParagraphSmall">
            <strong>Museu do Futebol:</strong> Localizado no saudoso estádio do
            Pacaembu, próximo ao bairro de{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/higienopolis"
              view="faq"
            >
              Higienópolis
            </ClickEventAnchor>
            , esse museu é um livro aberto e interativo sobre a história do
            futebol brasileiro;
          </Typography>

          <Typography variant="ParagraphSmall">
            <strong>Parque Ibirapuera:</strong> O maior e mais famoso parque da
            cidade de São Paulo, localizado no bairro da{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/vila-mariana"
              view="faq"
            >
              Vila Mariana
            </ClickEventAnchor>
            , conta com área verde bem ampla, lagos, trilhas para caminhada e
            museus. Ocasionalmente recebe shows dos mais renomados artistas da
            música brasileira e internacional;
          </Typography>

          <Typography variant="ParagraphSmall">
            <strong>Feira da Liberdade:</strong> Para quem ama a cultura
            japonesa, no bairro da{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/liberdade"
              view="faq"
            >
              Liberdade
            </ClickEventAnchor>{' '}
            acontece o evento da Feira da Liberdade, que oferece comidas
            típicas, artesanato e produtos relacionados à cultura japonesa. Além
            de ter eventos culturais, como apresentações de dança e música, tudo
            relacionado à cultura do Japão.
          </Typography>
        </div>
      ),
    },
    {
      ...defaultProps,
      title: 'Como escolher um imóvel para comprar em São Paulo?',
      content: (
        <div className={styles.ecFaqItem}>
          <Typography variant="ParagraphSmall">
            Antes de fechar a compra de um imóvel na maior cidade do país, nós
            recomendamos você levar em conta 2 pontos principais:
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Localização:</strong> Escolher o bairro onde você irá morar
            é uma decisão crucial, uma vez que influenciará significativamente
            na sua qualidade de vida. É essencial encontrar um bairro que atenda
            às suas necessidades e preferências.
          </Typography>
          <Typography variant="ParagraphSmall">
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/vila-madalena"
              view="faq"
            >
              Vila Madalena
            </ClickEventAnchor>
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/vila-olimpia"
              view="faq"
            >
              Vila Olímpia
            </ClickEventAnchor>{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/pinheiros"
              view="faq"
            >
              Pinheiros
            </ClickEventAnchor>{' '}
            são opções ideais. Eles oferecem uma ampla variedade de casas
            noturnas, bares e restaurantes, além de um fácil acesso ao
            transporte público da cidade.
          </Typography>
          <Typography variant="ParagraphSmall">
            Se você busca um ambiente mais tranquilo e residencial, bairros como{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/vila-mariana"
              view="faq"
            >
              Vila Mariana
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/paraiso"
              view="faq"
            >
              Paraíso
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/santa-cecilia"
              view="faq"
            >
              Santa Cecília
            </ClickEventAnchor>{' '}
            podem ser escolhas acertadas. Essas regiões se destacam pelas ruas
            arborizadas e ambiente pacífico, garantindo uma atmosfera relaxante
            em contraste com o agito da cidade. Tudo isso sem perder em
            facilidade de transporte público.
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Preço:</strong> O preço médio dos imóveis em São Paulo varia
            com base na localização, tipo e tamanho da propriedade. Bairros mais
            valorizados, como{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/jardins"
              view="faq"
            >
              Jardins
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/sp/sao-paulo/itaim-bibi"
              view="faq"
            >
              Itaim Bibi
            </ClickEventAnchor>{' '}
            <ClickEventAnchor
              target="blank"
              href="/regioes-atendidas/sp/sao-paulo/moema"
              view="faq"
            >
              Moema
            </ClickEventAnchor>
            , têm preços mais elevados, contando com apartamentos luxuosos e
            casas espaçosas com infraestrutura de alta qualidade. Se tiver mais
            flexibilidade na localização, é possível encontrar imóveis mais
            acessíveis e mais diversos. O preço médio do metro quadrado em São
            Paulo é de R$10.730.
          </Typography>
          <Typography variant="ParagraphSmall">
            Em resumo, a escolha do seu imóvel é uma decisão altamente pessoal
            na qual você deve ponderar o seu investimento financeiro e o estilo
            de vida que deseja desfrutar diariamente.
          </Typography>
        </div>
      ),
    },
  ],
  'rio-de-janeiro': [
    {
      ...defaultProps,
      title:
        'Quais são os bairros mais seguros para se morar no Rio de Janeiro?',
      content: (
        <div className={styles.ecFaqItem}>
          <Typography variant="ParagraphSmall">
            Segundo o{' '}
            <ClickEventAnchor
              target="blank"
              href="https://www.isp.rj.gov.br/"
              view="faq"
            >
              ISP
            </ClickEventAnchor>{' '}
            (Instituto de Segurança Pública), os bairros da Zona Sul e Oeste
            apresentam menores números relacionados à criminalidade, ou seja,
            são mais seguros para se viver. Abaixo listamos 3 bairros que são
            considerados seguros para morar no Rio de Janeiro:
          </Typography>

          <Typography variant="ParagraphSmall">
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/leblon"
              view="text"
            >
              <strong>Leblon:</strong>
            </ClickEventAnchor>{' '}
            Conhecido por suas praias incríveis, segurança, vida noturna e uma
            gastronomia de alta qualidade, o bairro{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/leblon"
              view="text"
            >
              Leblon
            </ClickEventAnchor>{' '}
            oferece uma ótima qualidade de vida, bem localizado na cidade
            maravilhosa e com uma vizinhança muito acolhedora. É um lugar muito
            desejado para morar devido a sua atmosfera sofisticada e amenidades;
          </Typography>

          <Typography variant="ParagraphSmall">
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/lagoa"
              view="text"
            >
              <strong>Lagoa:</strong>
            </ClickEventAnchor>{' '}
            Localizado na zona sul do Rio de Janeiro, tem como a sua principal
            atração turística a Lagoa Rodrigo de Freitas, além de uma área verde
            exuberante. É um bairro muito atraente para moradores que valorizam
            a tranquilidade e natureza, e prezam pela segurança;
          </Typography>

          <Typography variant="ParagraphSmall">
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/copacabana"
              view="text"
            >
              <strong>Copacabana:</strong>
            </ClickEventAnchor>{' '}
            O bairro mais famoso e conhecido do Rio de Janeiro, seja por sua
            icônica e mundialmente conhecida Praia de Copacabana, seja pela sua
            vida noturna animada ou gastronomia. Um local que é para moradores
            que gostam de um clima mais agitado, já que é um dos pontos
            turísticos mais visitados do Brasil;
          </Typography>

          <Typography variant="ParagraphSmall">
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/barra-da-tijuca"
              view="text"
            >
              <strong>Barra da Tijuca:</strong>
            </ClickEventAnchor>{' '}
            Um dos bairros mais modernos do Rio de Janeiro, conhecido pela sua
            praia extensa, arquitetura de luxo, várias opções de entretenimento
            e centros comerciais. É um local popular para moradores que buscam
            qualidade de vida e conveniência.
          </Typography>
        </div>
      ),
    },
    {
      ...defaultProps,
      title: 'Qual é o clima do Rio de Janeiro?',
      content: (
        <div className={styles.ecFaqItem}>
          <Typography variant="ParagraphSmall">
            O Rio de Janeiro tem um clima tropical com temperaturas agradáveis
            ao longo do ano. Os meses mais quentes ocorrem no verão, de dezembro
            a março, com chuvas frequentes. O inverno, de junho a agosto, é mais
            seco e um pouco mais fresco. A umidade é moderada, e a brisa do mar
            ajuda a aliviar o calor nas áreas costeiras.
          </Typography>
        </div>
      ),
    },
    {
      ...defaultProps,
      title:
        'Quais são as opções de lazer, cultura e entretenimento no Rio de Janeiro?',
      content: (
        <div className={styles.ecFaqItem}>
          <Typography variant="ParagraphSmall">
            As principais opções culturais, entretenimento e lazer no Rio são:
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Museu do Amanhã:</strong> Um dos mais famosos museus do Rio
            de Janeiro, projetado para explorar questões sobre o futuro da
            humanidade e do planeta. Ele oferece exposições interativas e
            experiências imersivas relacionadas a temas como sustentabilidade,
            tecnologia e mudanças climáticas.
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Parque Nacional da Tijuca:</strong> Localizado no Alto da
            Boa Vista, o Parque Nacional da Tijuca abriga uma floresta tropical
            exuberante, trilhas para caminhadas, cachoeiras e uma variedade de
            flora e fauna. É um refúgio natural no coração da cidade e oferece
            oportunidades para atividades ao ar livre, como caminhadas,
            observação de pássaros e contato com a natureza.
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Jeunesse Arena:</strong> A Jeunesse Arena é uma arena
            multiuso localizada na{' '}
            <ClickEventAnchor
              target="blank"
              href="regioes-atendidas/rj/rio-de-janeiro/barra-da-tijuca"
              view="faq"
            >
              Barra da Tijuca
            </ClickEventAnchor>
            . É um espaço importante que já recebeu eventos esportivos, como
            Circuito Master de Tênis, Competições de Jiu Jitsu e UFC. Além
            disso, é palco de shows dos mais renomados artistas mundiais, como
            The Weeknd, Harry Styles e entre outros.
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Parque Olímpico:</strong> Construído em 2016 para as
            Olimpíadas do Rio de Janeiro, o Parque está localizado na Barra da
            Tijuca. Ele abriga instalações esportivas de alto padrão, como o
            Estádio Olímpico, o Velódromo, a Arena Carioca 1 e 2, entre outras.
            Além de esportes, o parque é usado para eventos culturais, shows,
            como o Rock In Rio e atividades de lazer.
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Feira de São Cristóvão:</strong> Também conhecida como
            &quot;Feira dos Paraíbas&quot;, é um mercado e centro cultural que
            celebra a cultura nordestina do Brasil. Localizada no bairro de{' '}
            <ClickEventAnchor
              target="blank"
              href="regioes-atendidas/rj/rio-de-janeiro/sao-cristovao"
              view="faq"
            >
              São Cristóvão
            </ClickEventAnchor>
            , no Rio de Janeiro, oferece música ao vivo, dança, comida regional,
            artesanato e uma experiência cultural única. É um local vibrante
            onde os visitantes podem desfrutar da rica cultura e tradições do
            Nordeste brasileiro.
          </Typography>
        </div>
      ),
    },
    {
      ...defaultProps,
      title: 'Quais são os principais pontos turísticos do Rio de Janeiro?',
      content: (
        <div className={styles.ecFaqItem}>
          <Typography variant="ParagraphSmall">
            <strong>Cristo Redentor:</strong> Considerada uma das sete
            maravilhas do mundo moderno, o Cristo Redentor, localizado no morro
            do Corcovado, no bairro de{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/cosme-velho"
              view="text"
            >
              Cosme Velho
            </ClickEventAnchor>
            . O Cristo atrai visitas do mundo todo, com seus braços abertos, ele
            se tornou um símbolo mundial da cidade e do Brasil, oferecendo uma
            vista espetacular da cidade e da baía de Guanabara.
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Pão de Açúcar:</strong> Localizado no bairro da{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/bairro-da-urca"
              view="text"
            >
              Urca
            </ClickEventAnchor>{' '}
            e acessível por teleférico, o Pão de Açúcar oferece uma vista
            panorâmica espetacular da cidade, da Baía de Guanabara e das praias.
            Um dos principais pontos turísticos do Rio e um local imperdível
            para apreciar a beleza natural da cidade maravilhosa.
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Maracanã:</strong> Palco de 2 finais de copa do mundo, de
            final de copa libertadores e de diversos outros campeonatos, o
            icônico e mundialmente conhecido estádio do Maracanã, localizado no
            bairro{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/maracana"
              view="text"
            >
              Maracanã
            </ClickEventAnchor>
            , é um local com uma rica história e imperdível para os amantes de
            futebol.
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Sambódromo da Sapucaí:</strong> O Coração do carnaval
            carioca, o icônico espaço conhecido por sua pista para o desfile das
            escolas de samba do Rio de Janeiro durante o Carnaval. Possui uma
            atmosfera vibrante que celebra a cultura, a música e a dança
            brasileira.
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Quais são as praias mais famosas do Rio de Janeiro?</strong>
          </Typography>
          <Typography variant="ParagraphSmall">
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/praia-de-ipanema"
              view="text"
            >
              <strong>Praia de Ipanema:</strong>
            </ClickEventAnchor>{' '}
            Localizado no bairro{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/ipanema"
              view="text"
            >
              Ipanema
            </ClickEventAnchor>
            , uma das praias mais conhecidas do mundo todo, seja pela sua
            atmosfera sofisticada e por seu famoso calçadão com belos mosaicos.
          </Typography>
          <Typography variant="ParagraphSmall">
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/praia-de-copacabana"
              view="text"
            >
              <strong>Praia de Copacabana:</strong>
            </ClickEventAnchor>{' '}
            Um dos pontos mais icônicos da cidade maravilhosa, famosa por seu
            calçadão em formato de ondas e palco de grande eventos, como o
            famoso réveillon carioca. Essa praia icônica fica localizada no
            bairro de{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/copacabana"
              view="text"
            >
              Copacabana
            </ClickEventAnchor>
            , na zona sul do Rio de Janeiro.
          </Typography>
          <Typography variant="ParagraphSmall">
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/praia-do-leblon"
              view="text"
            >
              <strong>Praia do Leblon:</strong>
            </ClickEventAnchor>{' '}
            Uma das praias mais elegantes e tranquilas, localizada no bairro do{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/leblon"
              view="text"
            >
              Leblon
            </ClickEventAnchor>
            , é um local ideal para relaxar e apreciar as belas paisagens.
          </Typography>
          <Typography variant="ParagraphSmall">
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/praia-vermelha"
              view="text"
            >
              <strong>Praia Vermelha:</strong>
            </ClickEventAnchor>{' '}
            Localizada no bairro da{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/urca"
              view="text"
            >
              Urca
            </ClickEventAnchor>
            , a Praia Vermelha é muito conhecida por suas areias cercadas por
            montanhas, incluindo o Pão de Açúcar.
          </Typography>
        </div>
      ),
    },
    {
      ...defaultProps,
      title: 'Como funciona o transporte público no Rio de Janeiro?',
      content: (
        <div className={styles.ecFaqItem}>
          <div className={styles.ecFaqItem}>
            <Typography variant="ParagraphSmall">
              O transporte público no Rio de Janeiro inclui metrô, ônibus,
              trens, bondes, barcas e o VLT. O metrô é uma opção eficiente,
              atualmente a cidade conta com três linhas que conectam várias
              áreas da cidade:
            </Typography>

            <Typography variant="ParagraphSmall">
              <strong>Linha 1 Laranja:</strong> A primeira linha de metrô da
              cidade do Rio de Janeiro, ela conecta a Zona Norte à Zona Sul da
              cidade. Ela passa por bairros significativos, tornando-se uma
              principal opção de transporte público para moradores e turistas.
              Os principais bairros que essa linha atende são:{' '}
              <ClickEventAnchor
                href="/regioes-atendidas/rj/rio-de-janeiro/flamengo"
                view="text"
              >
                Flamengo
              </ClickEventAnchor>
              ,{' '}
              <ClickEventAnchor
                href="/regioes-atendidas/rj/rio-de-janeiro/botafogo"
                view="text"
              >
                Botafogo
              </ClickEventAnchor>
              ,{' '}
              <ClickEventAnchor
                href="/regioes-atendidas/rj/rio-de-janeiro/catete"
                view="text"
              >
                Catete
              </ClickEventAnchor>{' '}
              e{' '}
              <ClickEventAnchor
                href="/regioes-atendidas/rj/rio-de-janeiro/gloria"
                view="text"
              >
                Glória
              </ClickEventAnchor>
              . A EmCasa preparou um post falando mais dessa estação, confira{' '}
              <ClickEventAnchor
                target="blank"
                href="https://emcasa.com/blog/guia-de-bairros/linha-1-laranja-do-metrorio-guia-completo/"
                view="text"
              >
                aqui
              </ClickEventAnchor>{' '}
              para conferir.
            </Typography>

            <Typography variant="ParagraphSmall">
              <strong>Linha 2 Verde:</strong> Carinhosamente conhecida como
              Linha Verde, desenha um trajeto fascinante pelos corações
              pulsantes do Rio de Janeiro. Seu percurso se estende desde a
              estação Pavuna, na Zona Norte, até a estação Botafogo, na Zona
              Sul. Os principais bairros dessa linha são:{' '}
              <ClickEventAnchor
                href="/regioes-atendidas/rj/rio-de-janeiro/estacio"
                view="text"
              >
                Estácio
              </ClickEventAnchor>
              ,{' '}
              <ClickEventAnchor
                href="/regioes-atendidas/rj/rio-de-janeiro/maracana"
                view="text"
              >
                Maracanã
              </ClickEventAnchor>{' '}
              e{' '}
              <ClickEventAnchor
                href="/regioes-atendidas/rj/rio-de-janeiro/pavuna"
                view="text"
              >
                Pavuna
              </ClickEventAnchor>
              . A EmCasa preparou um post falando mais dessa estação, confira{' '}
              <ClickEventAnchor
                target="blank"
                href="https://emcasa.com/blog/guia-de-bairros/linha-2-verde-do-metrorio-guia-completo/"
                view="text"
              >
                aqui
              </ClickEventAnchor>{' '}
              para conferir.
            </Typography>

            <Typography variant="ParagraphSmall">
              <strong>Linha 4 Amarela:</strong> A Linha 4 Amarela do Metrô Rio,
              uma joia do sistema de metrô carioca, traça um itinerário de 16
              Km. Seu percurso majestoso une os bairros da Barra da Tijuca, na
              Zona Oeste, à sofisticação de Ipanema, na Zona Sul. Os principais
              bairros que essa linha atende são:{' '}
              <ClickEventAnchor
                href="/regioes-atendidas/rj/rio-de-janeiro/leblon"
                view="text"
              >
                Leblon
              </ClickEventAnchor>
              ,{' '}
              <ClickEventAnchor
                href="/regioes-atendidas/rj/rio-de-janeiro/ipanema"
                view="text"
              >
                Ipanema
              </ClickEventAnchor>{' '}
              e{' '}
              <ClickEventAnchor
                href="/regioes-atendidas/rj/rio-de-janeiro/barra-da-tijuca"
                view="text"
              >
                Barra da Tijuca
              </ClickEventAnchor>{' '}
              . A EmCasa preparou um post falando mais dessa estação, confira{' '}
              <ClickEventAnchor
                target="blank"
                href="https://emcasa.com/blog/guia-de-bairros/linha-4-amarela-do-metrorio-guia-completo/"
                view="faq"
              >
                aqui
              </ClickEventAnchor>{' '}
              para conferir.
            </Typography>

            <Typography variant="ParagraphSmall">
              Se você quer ter uma visão mais detalhada das linhas do metrô da
              cidade do Rio de Janeiro, confira{' '}
              <ClickEventAnchor
                target="blank"
                href="https://emcasa.com/blog/guia-de-bairros/metrorio-guia-completo-sobre-o-metro-do-rio-de-janeiro"
                view="faq"
              >
                aqui
              </ClickEventAnchor>{' '}
              o artigo que a EmCasa preparou sobre esse tema.
            </Typography>

            <Typography variant="ParagraphSmall">
              O que podemos destacar de diferencial dos transportes na Cidade
              Maravilhosa é o VLT (Veículo leve sobre trilhos), pois é um
              sistema de transporte moderno que atende ao centro do Rio,
              proporcionando uma opção eficiente para se locomover naquela
              região.
            </Typography>
          </div>
        </div>
      ),
    },
    {
      ...defaultProps,
      title: 'Como escolher um imovel para comprar no Rio de Janeiro?',
      content: (
        <div className={styles.ecFaqItem}>
          <Typography variant="ParagraphSmall">
            Antes de fechar a compra de um imóvel na cidade maravilhosa, nós
            recomendamos você levar em conta 2 pontos principais:
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Localização:</strong> O Rio de Janeiro é muito grande, e,
            por ser uma cidade turística, é bem agitado em determinadas regiões
            e épocas do ano. Então, pesquise bem o local que você irá comprar o
            seu imóvel, pois vai influenciar bastante no seu dia a dia, caso
            você more lá. Se você procura bairros mais tranquilos para morar,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/leblon"
              view="text"
            >
              Leblon
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/jardim-botanico"
              view="text"
            >
              Jardim Botânico
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/gavea"
              view="text"
            >
              Gávea
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/laranjeiras"
              view="text"
            >
              Laranjeiras
            </ClickEventAnchor>{' '}
            são uma das opções ideais, pois são conhecidos por ter uma atmosfera
            calma e serena, e oferecer aos seus moradores uma área residencial
            bem arborizada.
          </Typography>
          <Typography variant="ParagraphSmall">
            Agora, se você prefere bairros mais agitados, os bairros{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/ipanema"
              view="text"
            >
              Ipanema
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/botafogo"
              view="text"
            >
              Botafogo
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/santa-teresa"
              view="text"
            >
              Santa Teresa
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/leme"
              view="text"
            >
              Leme
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/copacabana"
              view="text"
            >
              Copacabana
            </ClickEventAnchor>{' '}
            são ideais, pois possuem uma vida noturna bem ativa com bares,
            restaurantes e baladas com músicas ao vivo e dança. Outras boas
            opções para quem gosta de ficar mais próximo do mar são os bairros{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/flamengo"
              view="text"
            >
              Flamengo
            </ClickEventAnchor>
            ,{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/catete"
              view="text"
            >
              Catete
            </ClickEventAnchor>{' '}
            e{' '}
            <ClickEventAnchor
              href="/regioes-atendidas/rj/rio-de-janeiro/gloria"
              view="text"
            >
              Glória
            </ClickEventAnchor>
            , além dos mais conhecidos, que são Copacabana e Ipanema.
          </Typography>
          <Typography variant="ParagraphSmall">
            <strong>Preço:</strong> Como bem sabemos, o preço de um imóvel é
            influenciado pela localização, pelo tipo do imóvel, se possui
            características especiais e pelo que o bairro em que ele está
            localizado oferece. Bairros como Leblon, Ipanema e Copacabana
            possuem imóveis mais luxuosos e mais espaçosos, porém com preços
            mais elevados em comparação com os demais bairros, muito devido ao
            que oferecem: praia, segurança e comércios.
          </Typography>
          <Typography variant="ParagraphSmall">
            Bairros na Zona Norte e Zona Oeste do Rio de Janeiro oferecem preços
            médios mais acessíveis, sendo opções atraentes para quem busca
            imóveis espaçosos e com um preço que cabe no bolso. No entanto,
            essas áreas podem ter variações significativas nos preços,
            dependendo da localização exata e da infraestrutura do bairro e do
            tipo de imóvel que você procura. O preço médio do metro quadrado no
            Rio de Janeiro é de R$10.288.
          </Typography>
          <Typography variant="ParagraphSmall">
            A decisão de escolher o seu imóvel em uma cidade tão magnífica como
            o Rio de Janeiro envolve dois elementos cruciais. Nossa recomendação
            é sempre dedicar um tempo considerável à pesquisa antes de fechar
            qualquer negócio. Além disso, é importante ter uma clara noção do
            investimento que pretende fazer para adquirir o seu imóvel. Dessa
            forma, o processo de compra se torna mais fluido e satisfatório.
          </Typography>
        </div>
      ),
    },
  ],
}
